import { merge } from 'lodash-es';
import React from 'react';

import type {
  ControlAppearance,
  ControlMaxWidth,
  ControlMinWidth,
  ControlSize,
  ControlVariantInput,
  ControlWidth,
} from '../shared/types';
import { stateZIndexes } from '../../common/z_indexes';
import { BaseInputContainer } from '../../components/BaseInput/BaseInput';
import { SelectContainer, SelectTrigger } from '../../components/Select/SelectTrigger';
import { styled } from '../../stitches.config';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import { ButtonContainer } from '../Button/Button';
import { ControlProvider } from '../shared/context';

const createChildSelector = (pseudoClass = '') => `
& ${ButtonContainer}${pseudoClass}:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true']), 
& ${SelectContainer}${pseudoClass} ${SelectTrigger}:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true']), 
& > ${SelectTrigger}${pseudoClass}:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true']), 
& ${BaseInputContainer}${pseudoClass}:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true'])`;

const createFocusedChildSelector = (pseudoClass = '') => `
& ${ButtonContainer}${pseudoClass}:focus-visible, 
& ${ButtonContainer}${pseudoClass}:has(:focus-visible), 
& ${ButtonContainer}${pseudoClass}[data-focus-visible='true'], 
& ${SelectContainer}${pseudoClass} ${SelectTrigger}:focus-visible, 
& ${SelectContainer}${pseudoClass} ${SelectTrigger}:has(:focus-visible), 
& ${SelectContainer}${pseudoClass} ${SelectTrigger}[data-focus-visible='true'], 
& > ${SelectTrigger}${pseudoClass}:focus-visible, 
& > ${SelectTrigger}${pseudoClass}:has(:focus-visible), 
& > ${SelectTrigger}${pseudoClass}[data-focus-visible='true'], 
& ${BaseInputContainer}${pseudoClass}:focus-within, 
& ${BaseInputContainer}${pseudoClass}:has(:focus-visible), 
& ${BaseInputContainer}${pseudoClass}[data-focus-visible='true']`;

const resetChildSelector = createChildSelector();
/**
 * Base UI `Menu.Trigger` injects focus-guard `<span>` siblings around the button while open,
 * so the trigger is no longer `:first-child` / `:last-child`. Use `:first-of-type` /
 * `:last-of-type` for buttons so joined `ControlGroup` radii still apply.
 */
const firstChildSelector = `
& ${ButtonContainer}:first-of-type:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true']), 
& ${SelectContainer}:first-child ${SelectTrigger}:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true']), 
& > ${SelectTrigger}:first-child:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true']), 
& ${BaseInputContainer}:first-child:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true'])`;
const lastChildSelector = `
& ${ButtonContainer}:last-of-type:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true']), 
& ${SelectContainer}:last-child ${SelectTrigger}:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true']), 
& > ${SelectTrigger}:last-child:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true']), 
& ${BaseInputContainer}:last-child:not(:focus-visible):not(:has(:focus-visible)):not([data-focus-visible='true'])`;
const hoveredChildSelector = createChildSelector(':hover');
const focusedChildSelector = createFocusedChildSelector();

const ControlGroupContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',

  [resetChildSelector]: {
    zIndex: stateZIndexes.base,
  },

  [hoveredChildSelector]: {
    zIndex: stateZIndexes.hover,
  },

  [focusedChildSelector]: {
    zIndex: stateZIndexes.focus,
  },

  variants: {
    direction: {
      row: {
        flexDirection: 'row',
      },
      column: {
        flexDirection: 'column',
      },
    },
    ghost: {
      true: {},
      false: {},
    },
    relation: {
      joined: {
        gap: '$1',
      },
      separate: {},
    },
    size: {
      'x-small': {},
      small: {},
      medium: {},
      large: {},
      'x-large': {},
    },
    wrap: {
      wrap: {
        flexWrap: 'wrap',
      },
      'no-wrap': {
        flexWrap: 'nowrap',
      },
    },
  },

  compoundVariants: [
    {
      ghost: false,
      relation: 'joined',
      css: {
        [resetChildSelector]: {
          borderRadius: 0,
        },
      },
    },
    {
      relation: 'separate',
      size: 'x-small',
      css: {
        '@notDesktop': {
          gap: '$6',
        },

        '@desktop': {
          gap: '$4',
        },
      },
    },
    {
      relation: 'separate',
      size: 'small',
      css: {
        '@notDesktop': {
          gap: '$8',
        },

        '@desktop': {
          gap: '$6',
        },
      },
    },
    {
      relation: 'separate',
      size: 'medium',
      css: {
        '@notDesktop': {
          gap: '$10',
        },

        '@desktop': {
          gap: '$8',
        },
      },
    },
    {
      relation: 'separate',
      size: 'large',
      css: {
        '@notDesktop': {
          gap: '$12',
        },

        '@desktop': {
          gap: '$10',
        },
      },
    },
    {
      relation: 'separate',
      size: 'x-large',
      css: {
        '@notDesktop': {
          gap: '$14',
        },

        '@desktop': {
          gap: '$12',
        },
      },
    },
    {
      direction: 'row',
      relation: 'joined',
      size: 'x-small',
      css: {
        [firstChildSelector]: {
          borderTopLeftRadius: '$6',
          borderBottomLeftRadius: '$6',
        },

        [lastChildSelector]: {
          borderTopRightRadius: '$6',
          borderBottomRightRadius: '$6',
        },
      },
    },
    {
      direction: 'row',
      relation: 'joined',
      size: 'small',
      css: {
        [firstChildSelector]: {
          borderTopLeftRadius: '$8',
          borderBottomLeftRadius: '$8',
        },

        [lastChildSelector]: {
          borderTopRightRadius: '$8',
          borderBottomRightRadius: '$8',
        },
      },
    },
    {
      direction: 'row',
      relation: 'joined',
      size: 'medium',
      css: {
        [firstChildSelector]: {
          borderTopLeftRadius: '$8',
          borderBottomLeftRadius: '$8',
        },

        [lastChildSelector]: {
          borderTopRightRadius: '$8',
          borderBottomRightRadius: '$8',
        },
      },
    },
    {
      direction: 'row',
      relation: 'joined',
      size: 'large',
      css: {
        [firstChildSelector]: {
          borderTopLeftRadius: '$10',
          borderBottomLeftRadius: '$10',
        },

        [lastChildSelector]: {
          borderTopRightRadius: '$10',
          borderBottomRightRadius: '$10',
        },
      },
    },
    {
      direction: 'row',
      relation: 'joined',
      size: 'x-large',
      css: {
        [firstChildSelector]: {
          borderTopLeftRadius: '$10',
          borderBottomLeftRadius: '$10',
        },

        [lastChildSelector]: {
          borderTopRightRadius: '$10',
          borderBottomRightRadius: '$10',
        },
      },
    },

    {
      direction: 'column',
      relation: 'joined',
      size: 'x-small',
      css: {
        [firstChildSelector]: {
          borderTopLeftRadius: '$6',
          borderTopRightRadius: '$6',
        },

        [lastChildSelector]: {
          borderBottomLeftRadius: '$6',
          borderBottomRightRadius: '$6',
        },
      },
    },
    {
      direction: 'column',
      relation: 'joined',
      size: 'small',
      css: {
        [firstChildSelector]: {
          borderTopLeftRadius: '$8',
          borderTopRightRadius: '$8',
        },

        [lastChildSelector]: {
          borderBottomLeftRadius: '$8',
          borderBottomRightRadius: '$8',
        },
      },
    },
    {
      direction: 'column',
      relation: 'joined',
      size: 'medium',
      css: {
        [firstChildSelector]: {
          borderTopLeftRadius: '$8',
          borderTopRightRadius: '$8',
        },

        [lastChildSelector]: {
          borderBottomLeftRadius: '$8',
          borderBottomRightRadius: '$8',
        },
      },
    },
    {
      direction: 'column',
      relation: 'joined',
      size: 'large',
      css: {
        [firstChildSelector]: {
          borderTopLeftRadius: '$10',
          borderTopRightRadius: '$10',
        },

        [lastChildSelector]: {
          borderBottomLeftRadius: '$10',
          borderBottomRightRadius: '$10',
        },
      },
    },
    {
      direction: 'column',
      relation: 'joined',
      size: 'x-large',
      css: {
        [firstChildSelector]: {
          borderTopLeftRadius: '$10',
          borderTopRightRadius: '$10',
        },

        [lastChildSelector]: {
          borderBottomLeftRadius: '$10',
          borderBottomRightRadius: '$10',
        },
      },
    },
  ],
});
ControlGroupContainer.displayName = 'ControlGroupContainer';

export type ControlGroupProps = {
  appearance?: ControlAppearance;
  children: React.ReactNode;
  direction?: 'row' | 'column';
  ghost?: boolean;
  size?: ControlSize;
  relation?: 'joined' | 'separate';
  variant?: ControlVariantInput;
  width?: ControlWidth;
  minWidth?: ControlMinWidth;
  maxWidth?: ControlMaxWidth;
  wrap?: 'wrap' | 'no-wrap';
};

export function ControlGroup({
  appearance,
  children,
  direction = 'row',
  ghost = false,
  relation = 'joined',
  size = 'medium',
  variant,
  width,
  minWidth,
  maxWidth,
  wrap,
  ...remaining
}: ControlGroupProps) {
  const css = merge(widthCSS(width), minWidthCSS(minWidth), maxWidthCSS(maxWidth));
  const effectiveWidth = width || (direction === 'column' && !width) ? '100%' : undefined;
  const effectiveMinWidth = minWidth || (direction === 'column' && !minWidth) ? 0 : undefined;
  const effectiveMaxWidth = maxWidth || (direction === 'column' && !maxWidth) ? '100%' : undefined;

  return (
    <ControlGroupContainer
      direction={direction}
      ghost={ghost}
      size={size}
      relation={relation}
      wrap={wrap}
      css={css}
      {...remaining}
    >
      <ControlProvider
        value={{
          appearance,
          ghost,
          size,
          variant,
          width: effectiveWidth,
          minWidth: effectiveMinWidth,
          maxWidth: effectiveMaxWidth,
        }}
      >
        {children}
      </ControlProvider>
    </ControlGroupContainer>
  );
}
